import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { Alert, ModalBody, ModalFooter } from 'reactstrap';
import {
  canChangeScheduledTime,
  ONE_OFF_CHANNEL_INFO,
  type SerializedAssetPublish,
} from 'fym-shared/src/music24/oneOffPublish';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { useDeleteAssetPublishMutation, useUpdateAssetPublishMutation } from '../../../../redux/api/fymAPI';
import { isSuccessfulResult } from '../../../../redux/api/utils/isSuccessfulResult';
import { publishMessages } from './publishMessages';
import { ONE_OFF_CHANNEL_NAMES, publishBadgeText, publishErrorText, publishFailText } from './publishBadgeText';
import { publishAtOfValue, PublishTimingField, PublishTimingValue } from './PublishTimingField';
import { useArtistTimeZone } from '../../../settings/tabs/artist/timeZone/useArtistTimeZone';

interface PublishStatusPanelProps {
  accountId: number;
  record: SerializedAssetPublish;
  releaseDate: string | null;
  onClose: () => void;
}

/**
 * A live record of an API channel: the state, "Open on YouTube", and before the video is public
 * "Change the time" and "Cancel the publish" (owner rule 2026-10-09). A login without the scope to
 * change a time says "Reconnect YouTube to change the time".
 */
const PublishStatusPanelComponent: React.FC<PublishStatusPanelProps> = ({
  accountId,
  record,
  releaseDate,
  onClose,
}) => {
  const intl = useIntl();
  const { channel } = record;
  const name = ONE_OFF_CHANNEL_NAMES[channel];
  const timeZone = useArtistTimeZone();
  const [isEditing, setIsEditing] = useState(false);
  const [timing, setTiming] = useState<PublishTimingValue>({ timing: record.timing, pickedAt: '' });
  const [update, updateState] = useUpdateAssetPublishMutation();
  const [remove, removeState] = useDeleteAssetPublishMutation();
  const badge = publishBadgeText(intl, channel, record);
  const changeable = canChangeScheduledTime(record, new Date());
  const needsReconnect =
    ONE_OFF_CHANNEL_INFO[channel].canChangeTime && record.status === 'scheduled' && !record.canChangeTime;
  const error = updateState.error ?? removeState.error;
  const publishAt = publishAtOfValue(timing, releaseDate, timeZone);

  const save = useCallback(async () => {
    const result = await update({
      accountId,
      publishUuid: record.uuid,
      body: { timing: timing.timing, publishAt },
    });
    if (isSuccessfulResult(result)) {
      setIsEditing(false);
    }
  }, [accountId, publishAt, record.uuid, timing.timing, update]);
  const cancel = useCallback(async () => {
    const result = await remove({ accountId, publishUuid: record.uuid });
    if (isSuccessfulResult(result)) {
      onClose();
    }
  }, [accountId, onClose, record.uuid, remove]);

  return (
    <>
      <ModalBody className="d-flex flex-column gap-3" data-testid="publish-status">
        <strong>{badge.text}</strong>
        {record.title !== null && <span className="text-muted">{record.title}</span>}
        {record.status === 'failed' && <span>{publishFailText(intl, record.failReason)}</span>}
        {needsReconnect && (
          <Alert color="info" className="mb-0 small">
            {intl.formatMessage(publishMessages.actionReconnect, { channel: name })}
          </Alert>
        )}
        {isEditing && (
          <PublishTimingField
            channel={channel}
            releaseDate={releaseDate}
            value={timing}
            onChange={setTiming}
            name="publish-change"
          />
        )}
        {error !== undefined && (
          <Alert color="danger" className="mb-0" data-testid="publish-error">
            {publishErrorText(intl, channel, error)}
          </Alert>
        )}
      </ModalBody>
      <ModalFooter className="flex-wrap gap-2">
        {record.externalUrl !== null && (
          <a className="btn btn-outline-secondary" href={record.externalUrl} target="_blank" rel="noreferrer">
            {intl.formatMessage(publishMessages.actionOpen, { channel: name })}
          </a>
        )}
        {changeable && !isEditing && (
          <SMLButton outline omitCap onClick={() => setIsEditing(true)}>
            {intl.formatMessage(publishMessages.actionChangeTime)}
          </SMLButton>
        )}
        {changeable && isEditing && (
          <SMLButton
            color="primary"
            omitCap
            disabled={updateState.isLoading}
            onClick={() => save().catch(console.error)}
          >
            {intl.formatMessage(publishMessages.submitSave)}
          </SMLButton>
        )}
        {(changeable || record.status === 'waiting') && (
          <SMLButton
            outline
            color="danger"
            omitCap
            disabled={removeState.isLoading}
            onClick={() => cancel().catch(console.error)}
          >
            {intl.formatMessage(publishMessages.actionCancel)}
          </SMLButton>
        )}
      </ModalFooter>
    </>
  );
};

export const PublishStatusPanel = TypedMemo(PublishStatusPanelComponent);
